import { useState } from 'react';
import { accountStatus } from './balance';
import { dayKey, formatMoney, netPnl, usd, tradingDayKey, currentTradingDay } from './pnl';
import { equitySeries, performanceStats, topInsight, unjournaledTrades } from './stats';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import InfoTip from './InfoTip';
import { showResult, tradeMoves } from './display';

const CURVE_W = 600;
const CURVE_H = 150;

function pnlClass(n) {
  return n > 0 ? 'gain' : n < 0 ? 'loss' : '';
}

// A stat tile with an optional small graphic on the right and/or a bar along the bottom.
export function Tile({ label, value, className = '', note, visual, bar, info }) {
  const { t } = useI18n();
  return (
    <div className={`stat${visual ? ' has-visual' : ''}`}>
      <div className="stat-main">
        <span className="stat-label">{t(label)}{info && <InfoTip text={t(info)} label={t(label)} />}</span>
        <span className={`stat-value ${className}`}>{value}</span>
        {note && <span className="stat-note">{note}</span>}
      </div>
      {visual && <div className="stat-visual" aria-hidden="true">{visual}</div>}
      {bar && <div className="stat-bar" aria-hidden="true">{bar}</div>}
    </div>
  );
}

// --- Tile graphics ---------------------------------------------------------

// Fills a ring to a share between 0 and 1.
export function Ring({ share }) {
  const r = 17;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 44 44" className="tile-ring">
      <circle cx="22" cy="22" r={r} className="tile-track" />
      {share > 0 && (
        <circle cx="22" cy="22" r={r} className="tile-ring-fill" strokeDasharray={`${c * share} ${c}`}
          transform="rotate(-90 22 22)" />
      )}
    </svg>
  );
}

// A half-circle gauge from 0 to 3 with a tick at 1.0, where wins and losses break even.
function ProfitGauge({ value }) {
  const max = 3;
  const share = value === Infinity ? 1 : Math.max(0, Math.min(value, max)) / max;
  const r = 20;
  const len = Math.PI * r;
  const arc = `M 4 26 A ${r} ${r} 0 0 1 44 26`;
  const tickAngle = Math.PI * (1 - 1 / max);
  const tx = 24 + Math.cos(tickAngle) * r;
  const ty = 26 - Math.sin(tickAngle) * r;
  return (
    <svg viewBox="0 0 48 30" className="tile-gauge">
      <path d={arc} className="tile-track" />
      {share > 0 && <path d={arc} className={`tile-gauge-fill ${value >= 1 ? 'up' : 'down'}`} strokeDasharray={`${len * share} ${len}`} />}
      <line x1={tx} y1={ty - 4} x2={tx} y2={ty + 4} className="tile-gauge-tick"
        transform={`rotate(${90 - (tickAngle * 180) / Math.PI} ${tx} ${ty})`} />
    </svg>
  );
}

// A small line of running P&L, trade by trade.
// wide: stretches to fill its space (the Score dashboard's Net P&L card).
export function Sparkline({ values, wide = false }) {
  if (values.length < 2) return null;
  const w = wide ? 300 : 84;
  const h = 52;
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const range = max - min || 1;
  const x = (i) => (i / (values.length - 1)) * w;
  const y = (v) => h - 2 - ((v - min) / range) * (h - 4);
  const line = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`tile-spark${wide ? ' wide' : ''}`} preserveAspectRatio={wide ? 'none' : undefined}>
      <line x1="0" x2={w} y1={y(0)} y2={y(0)} className="tile-zero" />
      <path d={line} className={`tile-spark-line ${values[values.length - 1] >= 0 ? 'up' : 'down'}`} />
    </svg>
  );
}

// Tiny green/red bars, one per value (today's trades, or this week's days).
function MiniBars({ values }) {
  if (!values.length) return null;
  const w = 84;
  const h = 52;
  const maxAbs = Math.max(1, ...values.map(Math.abs));
  const mid = h / 2;
  const slot = w / Math.max(values.length, 5);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="tile-bars">
      <line x1="0" x2={w} y1={mid} y2={mid} className="tile-zero" />
      {values.map((v, i) => {
        const bh = v === 0 ? 0 : Math.max(2.5, (Math.abs(v) / maxAbs) * (mid - 1));
        return <rect key={i} x={i * slot + slot * 0.15} width={slot * 0.7} y={v >= 0 ? mid - bh : mid} height={bh} rx="1.5"
          className={v >= 0 ? 'up' : 'down'} />;
      })}
    </svg>
  );
}

// A full-width bar split into wins and losses.
function SplitBar({ wins, losses }) {
  const total = wins + losses;
  if (!total) return null;
  return (
    <div className="tile-split">
      {wins > 0 && <span className="up" style={{ flexGrow: wins }} />}
      {losses > 0 && <span className="down" style={{ flexGrow: losses }} />}
    </div>
  );
}

// How big this average is next to the other one (average win vs average loss).
function CompareBar({ value, other, tone }) {
  if (!value) return null;
  const share = value / Math.max(value, other || 0);
  return (
    <div className="tile-compare">
      <span className={tone} style={{ width: `${share * 100}%` }} />
    </div>
  );
}

export function EquityCurve({ trades, startingBalance, cashFlows }) {
  const [hover, setHover] = useState(null);
  const points = equitySeries(trades, startingBalance, 30, startingBalance ? cashFlows : []);
  const base = startingBalance || 0;
  const values = points.map((p) => p.value);
  // The zero (or starting balance) line stays in view unless it's far from the line, so a
  // small move on a big running total doesn't flatten out.
  const span = Math.max(...values) - Math.min(...values);
  const showBase = base >= Math.min(...values) - span * 2 && base <= Math.max(...values) + span * 2;
  const min = showBase ? Math.min(base, ...values) : Math.min(...values);
  const max = showBase ? Math.max(base, ...values) : Math.max(...values);
  const pad = (max - min) * 0.1 || 1;
  const lo = min - pad;
  const hi = max + pad;
  const x = (i) => (i / (points.length - 1)) * CURVE_W;
  const y = (v) => CURVE_H - ((v - lo) / (hi - lo)) * CURVE_H;
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join('');
  const area = `${line}L${CURVE_W},${CURVE_H}L0,${CURVE_H}Z`;
  // Trading result over the 30 trading days; deposits and withdrawals move the line but aren't P&L.
  const change = points.reduce((sum, p) => sum + p.dayNet, 0);
  const { t } = useI18n();
  const label = (p) => p.date.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });

  function onMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - rect.left) / rect.width) * (points.length - 1));
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  }

  const h = hover !== null ? points[hover] : null;

  return (
    <div className="equity">
      <div className="equity-head">
        <span className="stat-label">{startingBalance ? t('Balance, last 30 trading days') : t('Cumulative P&L, last 30 trading days')}<InfoTip label={startingBalance ? t('Balance, last 30 trading days') : t('Cumulative P&L, last 30 trading days')}
          text={startingBalance ? t('Your account balance at the end of each of your last 30 trading days, after fees. Days without trades are skipped. The number on the right is how much your trades made or lost over them.') : t('Your running total of net P&L at the end of each of your last 30 trading days. Days without trades are skipped. The number on the right is how much it changed over them.')} /></span>
        <span className={`equity-change ${pnlClass(change)}`}>{formatMoney(change)}</span>
      </div>
      <div className="equity-plot" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${CURVE_W} ${CURVE_H}`} preserveAspectRatio="none" role="img"
          aria-label={`${startingBalance ? t('Balance, last 30 trading days') : t('Cumulative P&L, last 30 trading days')}: ${formatMoney(change)}`}>
          <defs>
            <linearGradient id="equity-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0e93a6" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#0e93a6" stopOpacity="0" />
            </linearGradient>
          </defs>
          {showBase && <line x1="0" x2={CURVE_W} y1={y(base)} y2={y(base)} className="equity-baseline" />}
          <path d={area} fill="url(#equity-fill)" />
          <path d={line} className="equity-line" />
          {h && (
            <>
              <line x1={x(hover)} x2={x(hover)} y1="0" y2={CURVE_H} className="equity-crosshair" />
              <circle cx={x(hover)} cy={y(h.value)} r="4" className="equity-dot" />
            </>
          )}
        </svg>
        {h && (
          <div className={`equity-tooltip${hover > points.length * 0.7 ? ' align-right' : ''}`}
            style={{ left: `${(hover / (points.length - 1)) * 100}%` }}>
            <span>{label(h)}</span>
            <strong>{startingBalance ? usd.format(h.value) : formatMoney(h.value)}</strong>
            {h.dayNet !== 0 && <span className={pnlClass(h.dayNet)}>{t('Day {amount}', { amount: formatMoney(h.dayNet) })}</span>}
          </div>
        )}
      </div>
      <div className="equity-axis">
        <span>{label(points[0])}</span>
        <span>{label(points[points.length - 1])}</span>
      </div>
    </div>
  );
}

// One bar per trading day (a day with trades) for the last 30 of them: green above zero, red below.
export function DailyBars({ trades }) {
  const { t } = useI18n();
  const [hover, setHover] = useState(null);
  const counts = {};
  const nets = {};
  for (const x of trades) {
    const key = tradingDayKey(x.date);
    counts[key] = (counts[key] || 0) + 1;
    nets[key] = (nets[key] || 0) + netPnl(x);
  }
  const points = Object.keys(counts).sort().slice(-30).map((key) => ({ key, date: new Date(`${key}T12:00:00`), dayNet: nets[key] }));
  const max = Math.max(0, ...points.map((p) => p.dayNet));
  const min = Math.min(0, ...points.map((p) => p.dayNet));
  const range = max - min || 1;
  const zero = (max / range) * 100; // the zero line, as % from the top
  const green = points.filter((p) => p.dayNet > 0).length;
  const red = points.filter((p) => p.dayNet < 0).length;
  const traded = points.length;
  const label = (p) => p.date.toLocaleDateString(currentLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
  const slot = 100 / Math.max(points.length, 1);
  const short = (p) => p.date.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
  const h = hover !== null ? points[hover] : null;

  return (
    <div className="equity daily-bars">
      <div className="equity-head">
        <span className="stat-label">{t('Daily P&L, last 30 trading days')}<InfoTip label={t('Daily P&L, last 30 trading days')} text={t('Each bar is one day you traded, for your last 30 trading days: how much you made (green) or lost (red) that day, after fees. Days without trades are skipped.')} /></span>
        {traded > 0 && (
          <span className="daily-count"><span className="gain">{t('{n} green', { n: green })}</span> &middot; <span className="loss">{t('{n} red', { n: red })}</span></span>
        )}
      </div>
      <div className="equity-plot bars-plot" onMouseLeave={() => setHover(null)} role="img"
        aria-label={t('Daily P&L over the last 30 trading days: {green} green days and {red} red days', { green, red })}>
        {traded === 0 && <span className="bars-empty">{t('No trades yet')}</span>}
        <span className="bars-zero" style={{ top: `${zero}%` }} />
        {points.map((p, i) => {
          const height = (Math.abs(p.dayNet) / range) * 100;
          return (
            <div key={p.key} className={`bar-slot${hover === i ? ' hover' : ''}`} style={{ left: `${i * slot}%`, width: `${slot}%` }}
              onMouseEnter={() => setHover(i)} onClick={() => setHover(i)}>
              {p.dayNet !== 0 && (
                <span className={`bar ${p.dayNet > 0 ? 'up' : 'down'}`}
                  style={p.dayNet > 0 ? { bottom: `${100 - zero}%`, height: `${height}%` } : { top: `${zero}%`, height: `${height}%` }} />
              )}
            </div>
          );
        })}
        {h && (
          <div className={`equity-tooltip${hover > points.length * 0.7 ? ' align-right' : ''}`}
            style={{ left: `${(hover + 0.5) * slot}%` }}>
            <span>{label(h)}</span>
            <strong className={pnlClass(h.dayNet)}>{formatMoney(h.dayNet)}</strong>
            <span>{t(counts[h.key] === 1 ? '{n} trade' : '{n} trades', { n: counts[h.key] })}</span>
          </div>
        )}
      </div>
      <div className="equity-axis">
        <span>{points.length ? short(points[0]) : ''}</span>
        <span>{points.length ? short(points[points.length - 1]) : ''}</span>
      </div>
    </div>
  );
}

// The eight stat tiles, by id, so the Dashboard can show them together (PerformancePanel) or
// one at a time as widgets (DashboardWidgets.js).
export function performanceTiles(trades, size, t) {
  const s = performanceStats(trades);
  const pf = s.profitFactor === null ? '—' : s.profitFactor === Infinity ? '∞' : s.profitFactor.toFixed(2);
  const ratio = s.avgWin && s.avgLoss ? s.avgWin / s.avgLoss : null;

  // Data for the tile graphics.
  const byTime = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  let running = 0;
  const runningNet = [0, ...byTime.map((x) => (running += netPnl(x)))];
  const wins = trades.filter((x) => netPnl(x) > 0).length;
  const losses = trades.filter((x) => netPnl(x) < 0).length;
  const todayKey = currentTradingDay();
  const todayNets = byTime.filter((x) => tradingDayKey(x.date) === todayKey).map(netPnl);
  const weekStart = new Date();
  weekStart.setHours(12, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekNets = [];
  for (let d = new Date(weekStart); dayKey(d) <= todayKey; d.setDate(d.getDate() + 1)) {
    const key = dayKey(d);
    weekNets.push(trades.filter((x) => tradingDayKey(x.date) === key).reduce((sum, x) => sum + netPnl(x), 0));
  }

  // Results in the chosen display (dollars, % of account, points, ticks or hidden).
  const show = (list, net, average = false) => showResult({ net, moves: tradeMoves(list), size, count: list.length, average, money: average ? usd.format : formatMoney, t });
  const winners = trades.filter((x) => netPnl(x) > 0);
  const losers = trades.filter((x) => netPnl(x) < 0);
  const todayTrades = trades.filter((x) => tradingDayKey(x.date) === todayKey);
  const weekFrom = new Date(weekStart);
  weekFrom.setHours(0, 0, 0, 0);
  const weekTrades = trades.filter((x) => new Date(x.date) >= weekFrom);
  const netShown = show(trades, s.net);
  const avgWinShown = s.avgWin === null ? null : show(winners, s.avgWin * winners.length, true);
  const avgLossShown = s.avgLoss === null ? null : show(losers, -s.avgLoss * losers.length, true);
  const todayShown = show(todayTrades, s.today);
  const weekShown = show(weekTrades, s.week);
  // For the wide Net P&L card: this month, fees paid and the average trade.
  const monthKey = todayKey.slice(0, 7);
  const monthTrades = trades.filter((x) => tradingDayKey(x.date).slice(0, 7) === monthKey);
  const monthShown = show(monthTrades, monthTrades.reduce((sum, x) => sum + netPnl(x), 0));
  const fees = trades.reduce((sum, x) => sum + (x.fees || 0), 0);
  const perTradeShown = trades.length ? show(trades, s.net, true) : null;

  return {
    net: <Tile key="net" label="Net P&L (after fees)" info="Everything these trades made or lost, after commissions and fees. The line shows your running total, trade by trade." value={netShown.text} className={netShown.tone}
          visual={trades.length > 1 ? <Sparkline values={runningNet} /> : null} />,
    netWide: <Tile key="netWide" label="Net P&L (after fees)" info="Everything these trades made or lost, after commissions and fees. The line shows your running total, trade by trade." value={netShown.text} className={netShown.tone}
          bar={(
            <span className="net-wide-facts">
              <span>{t('This month')} <b className={monthShown.tone}>{monthShown.text}</b></span>
              <span>{t('Fees paid')} <b>{formatMoney(fees).replace(/^\+/, '')}</b></span>
              {perTradeShown && <span>{t('Per trade')} <b className={perTradeShown.tone}>{perTradeShown.text}</b></span>}
            </span>
          )}
          visual={trades.length > 1 ? <Sparkline values={runningNet} wide /> : null} />,
    trades: <Tile key="trades" label="Trades" info="How many trades you took: W are winners, L are losers. The bar shows the mix, green for wins and red for losses." value={s.count} note={s.count ? t('{w} W · {l} L', { w: wins, l: losses }) : ''}
          bar={s.count ? <SplitBar wins={wins} losses={losses} /> : null} />,
    winRate: <Tile key="winRate" label="Win rate" info="The share of your trades that made money. Trades that broke even don't count as wins." value={s.winRate === null ? '—' : `${Math.round(s.winRate)}%`}
          visual={s.winRate === null ? null : <Ring share={s.winRate / 100} />} />,
    profitFactor: <Tile key="profitFactor" label="Profit factor" info="What your winners made divided by what your losers lost. Above 1.0 you're making money; 1.5 or more is strong. The mark on the gauge is 1.0." value={pf}
          className={s.profitFactor === null ? '' : s.profitFactor >= 1 ? 'gain' : 'loss'}
          note={s.profitFactor === null ? '' : t('Gross wins ÷ gross losses')}
          visual={s.profitFactor === null ? null : <ProfitGauge value={s.profitFactor} />} />,
    avgWin: <Tile key="avgWin" label="Average win" info="How much you make on a typical winning trade. The bar compares it with your average loss." value={avgWinShown ? avgWinShown.text.replace(/^\+/, '') : '—'} className="gain"
          bar={s.avgWin ? <CompareBar value={s.avgWin} other={s.avgLoss} tone="up" /> : null} />,
    avgLoss: <Tile key="avgLoss" label="Average loss" info="How much you lose on a typical losing trade. You want it smaller than your average win." value={avgLossShown ? (avgLossShown.text.startsWith('-') ? avgLossShown.text : `-${avgLossShown.text}`) : '—'} className="loss"
          note={ratio ? t('Wins are {ratio}× your losses', { ratio: ratio.toFixed(1) }) : ''}
          bar={s.avgLoss ? <CompareBar value={s.avgLoss} other={s.avgWin} tone="down" /> : null} />,
    today: <Tile key="today" label="Today" info="Your net P&L for today's session. A new trading day starts at 6 PM New York time, like the futures market. Each bar is one trade." value={todayShown.text} className={todayShown.tone}
          visual={todayNets.length ? <MiniBars values={todayNets} /> : null} />,
    week: <Tile key="week" label="This week" info="Your net P&L since Sunday. Each bar is one day: green for a winning day, red for a losing day." value={weekShown.text} className={weekShown.tone}
          visual={weekNets.some((v) => v !== 0) ? <MiniBars values={weekNets} /> : null} />,
  };
}

export function PerformancePanel({ trades, account, scope, size }) {
  const { t } = useI18n();
  const tiles = performanceTiles(trades, size, t);
  return (
    <section className="performance">
      {scope && <p className="performance-scope">{scope}</p>}
      <div className="performance-tiles">
        {Object.entries(tiles).filter(([id]) => id !== 'netWide').map(([, tile]) => tile)}
      </div>
      <div className="performance-charts">
        <EquityCurve trades={trades} startingBalance={account && account.size ? account.size : null} cashFlows={account ? account.cashFlows : []} />
        <DailyBars trades={trades} />
      </div>
    </section>
  );
}

export function InsightCard({ trades, library, onOpen, recent = true, scopeLine = '' }) {
  const { t } = useI18n();
  const insight = topInsight(trades, library, recent);
  return (
    <section className={`insight${insight && insight.kind !== 'none' ? ' has-insight' : ''}${onOpen ? ' clickable' : ''}`}
      onClick={onOpen} role={onOpen ? 'link' : undefined} tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => onOpen && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen())}>
      <div className="insight-head">
        <span className="insight-label">
          {t('Insight')}
          {insight && insight.sample < 10 && <span className="finding-small">{t('Early observation')}</span>}
        </span>
        {onOpen && <span className="insight-more">{t('See full insights')} &rarr;</span>}
      </div>
      {insight ? (
        <>
          <p className="insight-text">{insight.text}</p>
          {insight.tip && <p className="insight-tip">{insight.tip}</p>}
        </>
      ) : (
        <p className="insight-text muted">{recent ? t('Log at least 10 trades in the last 30 days to unlock your first insight.') : t('At least 10 trades are needed for an insight. Widen the date range or remove a filter.')}</p>
      )}
      {scopeLine && <p className="insight-scope-line">{t('Based on {scope}', { scope: scopeLine })}</p>}
    </section>
  );
}

export function Nudges({ trades, account, onEditTrade, showUnjournaled = true }) {
  const { t, tr } = useI18n();
  const unjournaled = showUnjournaled ? unjournaledTrades(trades) : [];
  const status = account ? accountStatus(account, trades) : null;
  const payout = status && status.payoutNeeded ? status : null;
  if (!unjournaled.length && !payout) return null;

  return (
    <section className="nudges">
      {unjournaled.length > 0 && (
        <div className="nudge">
          <span>
            {tr(unjournaled.length === 1 ? '<0>{n}</0> trade waiting for tags or notes.' : '<0>{n}</0> trades waiting for tags or notes.',
              [(x) => <strong>{x}</strong>], { n: unjournaled.length })}
          </span>
          <button type="button" className="secondary small" onClick={() => onEditTrade(unjournaled[0])}>
            {t('Journal the latest')}
          </button>
        </div>
      )}
      {payout && (
        <div className="nudge">
          {payout.payoutDays >= payout.payoutNeeded ? (
            <span>{tr('<0>Payout eligible:</0> {n} winning days of {min}+.', [(x) => <strong className="gain">{x}</strong>],
              { n: payout.payoutDays, min: usd.format(payout.payoutMin) })}</span>
          ) : (
            <span>
              {tr(payout.payoutMin
                ? '<0>{n}</0> more winning days of {min}+ until you can request a payout ({done}/{needed}).'
                : '<0>{n}</0> more winning days until you can request a payout ({done}/{needed}).',
              [(x) => <strong>{x}</strong>], {
                n: payout.payoutNeeded - payout.payoutDays, min: payout.payoutMin ? usd.format(payout.payoutMin) : '',
                done: payout.payoutDays, needed: payout.payoutNeeded,
              })}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
